import { ProcessTagBadge } from '@components/process-tag-badge';
import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@components/ui/popover';
import { Separator } from '@components/ui/separator';
import { ChevronsUpDown } from 'lucide-react';
import { observer } from 'mobx-react';

import theme from '@/styles/theme';

type Item = { id: string; name?: string; tag?: string | null; icon?: string };
type HeaderDropdownProps = {
  items: Item[];
  currentItemId: string;
  onSelect: (id: string) => void;
  type: 'process' | 'workflow';
};

const getInitials = (name?: string): string => {
  if (!name) {
    return '';
  }
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return (words[0]?.[0] ?? '').toUpperCase();
};

export const HeaderDropdown = observer(
  ({ items, currentItemId, onSelect, type }: HeaderDropdownProps) => (
    <Popover>
      <PopoverTrigger>
        <div className="m-1 w-full cursor-pointer select-none rounded-sm py-1 pl-2 hover:bg-gray-100">
          <ChevronsUpDown color={theme.color.grey[3]} size={14} />
        </div>
      </PopoverTrigger>
      <PopoverContent className="z-50 w-[260px] p-0">
        <Command className="border-none shadow-none" loop>
          <CommandInput
            placeholder={`${type === 'process' ? 'Process' : 'Workflow'} name`}
          />
          <Separator />
          <CommandList>
            <CommandEmpty>
              {' '}
              No {type === 'process' ? 'processes' : 'workflows'} found.
            </CommandEmpty>
            <CommandGroup
              heading={type === 'process' ? 'Processes' : 'Workflows'}
            >
              {items.map(item => (
                <CommandItem
                  className="gap-2"
                  defaultChecked={currentItemId === item.id}
                  key={item.id}
                  keywords={item.name ? [item.name] : undefined}
                  onSelect={() => onSelect(item.id)}
                  value={item.id}
                >
                  <Avatar className="h-5 w-5 shrink-0 text-[9px]">
                    <AvatarImage className="object-cover" src={item.icon} />
                    <AvatarFallback className="bg-primary/10 font-medium text-[9px] text-primary">
                      {getInitials(item.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{item.name}</span>
                  <ProcessTagBadge className="ml-auto" tag={item.tag ?? null} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
);
